import type { ToggleOption } from "@replo/design-system/components/toggle/ToggleGroup";
import type { TextAlignmentModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useDefaultSelectedElementDesignation } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

const VALID_ALIGNMENTS = ["left", "center", "right", "justify"] as const;
type ValidAlignment = (typeof VALID_ALIGNMENTS)[number];

/**
 * NOTE (Kurt, 2025-07-24): Maps CSS text-align values to display values for the UI
 * CSS supports logical values (start/end) that are direction-aware, but our UI
 * only displays physical directions (left/right). We map start→left and end→right
 * assuming LTR text direction. This is acceptable since the editor primarily
 * targets LTR languages and users can still select the desired visual alignment
 * from the UI.
 *
 * If users complain about this and are using RTL languages we can add support for
 * the logical values to the UI and account for RTL text alignment.
 */
const getDisplayValue = (cssValue: string | null): string | null => {
  if (!cssValue) {
    return null;
  }

  switch (cssValue) {
    case "start":
      return "left";
    case "end":
      return "right";
    default:
      return cssValue;
  }
};

interface TextAlignmentModifierProps {
  onChange: (modifiers: TextAlignmentModifierValue[]) => void;
}

const TextAlignmentModifierComponent: React.FC<TextAlignmentModifierProps> = ({
  onChange,
}) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const currentValue = getDisplayValue(
    defaultDesignation?.element.styleOverrides?.textAlign ?? null,
  );

  const [alignment, setAlignment] = useOverridableState(
    currentValue ?? "",
    (newAlignment: string) => {
      onChange([
        {
          type: "alignment" as const,
          value: newAlignment as ValidAlignment,
        },
      ]);
    },
  );

  const alignmentOptions: ToggleOption[] = [
    {
      value: "left",
      label: <AlignLeft size={16} />,
      tooltipContent: "Align Left",
    },
    {
      value: "center",
      label: <AlignCenter size={16} />,
      tooltipContent: "Align Center",
    },
    {
      value: "right",
      label: <AlignRight size={16} />,
      tooltipContent: "Align Right",
    },
    {
      value: "justify",
      label: <AlignJustify size={16} />,
      tooltipContent: "Justify",
    },
  ];

  return (
    <div className="alignment-modifier">
      <ToggleGroup
        size="sm"
        selectedValue={alignment}
        options={alignmentOptions}
        onChange={setAlignment}
        layoutClassName="w-full"
      />
    </div>
  );
};

export default TextAlignmentModifierComponent;
